"use client"

import * as React from "react"
import { ChevronRight, LucideIcon } from "lucide-react"
import {
  HomeOutlined,
  PersonOutline,
  CabinOutlined,
  ScaleOutlined,
  CorporateFare,
  AccountTree,
  Security,
  SelectAll,
  NotificationsActive,
  Assessment,
  ControlCamera,
  DriveFileRenameOutline,
  Dashboard,
  IntegrationInstructions,
  BugReport,
  TheaterComedy,
  Settings,
  Brush,
  ErrorOutline,
  Kitchen,
} from "@mui/icons-material"
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined"
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded"
import QueryStatsIcon from "@mui/icons-material/QueryStats"
import CallMergeIcon from "@mui/icons-material/CallMerge"
import { IconMailFast } from "@tabler/icons-react"

import { cn } from "@/lib/utils"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

// Define channel and item types
export interface NavItem {
  id: string
  name: string
  icon?: React.ReactNode
  clickEventName: string
  isTeam?: boolean
}

export interface NavGrouping {
  id: string
  name: string
  icon?: React.ReactNode
  items: NavItem[]
  displayOrder?: number
  isTeam?: boolean
}

export interface NavChannel {
  id: string
  name: string
  icon?: React.ReactNode
  items: NavItem[]
  groupings?: NavGrouping[]
  displayOrder?: number
}

// Icon mapping utility
const iconMap: Record<string, React.ReactNode> = {
  "home": <HomeOutlined fontSize="small" />,
  "person": <PersonOutline fontSize="small" />,
  "cabin": <CabinOutlined fontSize="small" />,
  "scale": <ScaleOutlined fontSize="small" />,
  "corporate": <CorporateFare fontSize="small" />,
  "tree": <AccountTree fontSize="small" />,
  "security": <Security fontSize="small" />,
  "select": <SelectAll fontSize="small" />,
  "notification": <NotificationsActive fontSize="small" />,
  "assessment": <Assessment fontSize="small" />,
  "control": <ControlCamera fontSize="small" />,
  "rename": <DriveFileRenameOutline fontSize="small" />,
  "dashboard": <Dashboard fontSize="small" />,
  "integration": <IntegrationInstructions fontSize="small" />,
  "bug": <BugReport fontSize="small" />,
  "theater": <TheaterComedy fontSize="small" />,
  "settings": <Settings fontSize="small" />,
  "brush": <Brush fontSize="small" />,
  "error": <ErrorOutline fontSize="small" />,
  "kitchen": <Kitchen fontSize="small" />,
  "workload": <AssuredWorkloadOutlinedIcon fontSize="small" />,
  "time": <AccessTimeOutlinedIcon fontSize="small" />,
  "apartment": <ApartmentOutlinedIcon fontSize="small" />,
  "grid": <GridViewRoundedIcon fontSize="small" />,
  "stats": <QueryStatsIcon fontSize="small" />,
  "merge": <CallMergeIcon fontSize="small" />,
  "mail": <IconMailFast size={18} />,
}

interface NavMainProps {
  channels: NavChannel[]
  selectedContainer: string
  selectedOption: string
  onContainerChange: (container: string) => void
  onOptionChange: (option: string) => void
  hasTeamsAccess?: boolean
  className?: string
}

export function NavMain({
  channels,
  selectedContainer,
  selectedOption,
  onContainerChange,
  onOptionChange,
  hasTeamsAccess = false,
  className,
}: NavMainProps) {
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"

  // Track expanded states - initialize with first channel expanded
  const [expandedChannels, setExpandedChannels] = React.useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    // Expand the first channel by default (General)
    if (channels.length > 0) {
      initial[channels[0].id] = true
    }
    return initial
  })
  const [expandedGroupings, setExpandedGroupings] = React.useState<Record<string, boolean>>({})

  // Auto-expand when selectedOption changes (e.g., when user clicks an item in a different channel)
  React.useEffect(() => {
    const channel = channels.find(c => c.name === selectedOption)
    if (channel && expandedChannels[channel.id] === undefined) {
      // Only auto-expand if the channel hasn't been explicitly toggled
      setExpandedChannels(prev => ({ ...prev, [channel.id]: true }))
    }
  }, [selectedOption, channels])

  // Auto-expand grouping when selectedContainer is one of its items
  React.useEffect(() => {
    if (!selectedContainer) return
    channels.forEach(channel => {
      channel.groupings?.forEach(grouping => {
        const hasActiveItem = grouping.items.some(item => item.clickEventName === selectedContainer)
        if (hasActiveItem && !expandedGroupings[grouping.id]) {
          setExpandedGroupings(prev => ({ ...prev, [grouping.id]: true }))
        }
      })
    })
  }, [selectedContainer, channels])

  const toggleChannel = (channelId: string) => {
    setExpandedChannels(prev => {
      const currentState = prev[channelId] ?? false
      return {
        ...prev,
        [channelId]: !currentState
      }
    })
  }

  const toggleGrouping = (groupingId: string) => {
    setExpandedGroupings(prev => ({
      ...prev,
      [groupingId]: !prev[groupingId]
    }))
  }

  const handleItemClick = (item: NavItem, channel: NavChannel) => {
    // Only update selectedOption if it's different, but don't force it
    // This prevents the channel from staying highlighted when items are clicked
    if (selectedOption !== channel.name) {
      onOptionChange(channel.name)
    }
    onContainerChange(item.clickEventName)
  }

  // Separate team items from regular items
  const separateTeamItems = (items: NavItem[]) => {
    const regularItems = items.filter(item => !item.isTeam)
    const teamItems = items.filter(item => item.isTeam)
    return { regularItems, teamItems }
  }

  const renderNavItem = (item: NavItem, channel: NavChannel, isSubItem = false) => {
    const isActive = selectedContainer === item.clickEventName
    
    if (isCollapsed && !isSubItem) {
      return (
        <TooltipProvider key={item.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarMenuButton
                onClick={() => handleItemClick(item, channel)}
                isActive={isActive}
                className={cn(
                  "h-9 w-9 p-0 justify-center",
                  isActive && "bg-muted"
                )}
              >
                <span className="flex items-center justify-center">
                  {item.icon}
                </span>
              </SidebarMenuButton>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {item.name}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    if (isSubItem) {
      return (
        <Button
          key={item.id}
          variant="ghost"
          onClick={() => handleItemClick(item, channel)}
          className={cn(
            "w-full justify-start",
            isActive
              ? "bg-muted text-foreground"
              : "text-foreground hover:bg-muted/70"
          )}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontFamily: 'Figtree, sans-serif' }}
        >
          <span className="truncate" style={{ fontSize: '13px', fontWeight: 'normal', color: 'inherit' }}>
            {item.name}
          </span>
        </Button>
      )
    }

    return (
      <SidebarMenuItem key={item.id}>
        <Button
          variant="ghost"
          onClick={() => handleItemClick(item, channel)}
          className={cn(
            "w-full justify-start cursor-pointer",
            isActive
              ? "bg-muted text-foreground"
              : "text-foreground hover:bg-muted/70"
          )}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontFamily: 'Figtree, sans-serif' }}
        >
          <span className="flex items-center justify-center [&>svg]:size-4 [&>svg]:shrink-0">
            {item.icon}
          </span>
          <span className="truncate" style={{ fontSize: '13px', fontWeight: 'normal', color: 'inherit' }}>{item.name}</span>
        </Button>
      </SidebarMenuItem>
    )
  }

  const renderGrouping = (grouping: NavGrouping, channel: NavChannel) => {
    const isExpanded = expandedGroupings[grouping.id] || false
    const { regularItems, teamItems } = separateTeamItems(grouping.items)

    if (isCollapsed) {
      return (
        <SidebarMenuItem key={grouping.id}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarMenuButton className="h-9 w-9 p-0 justify-center">
                  <span className="flex items-center justify-center">
                    {grouping.icon || <ChevronRight className="h-4 w-4" />}
                  </span>
                </SidebarMenuButton>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                {grouping.name}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </SidebarMenuItem>
      )
    }

    return (
      <Collapsible
        key={grouping.id}
        open={isExpanded}
        onOpenChange={() => toggleGrouping(grouping.id)}
        className="group/collapsible"
        data-grouping-id={grouping.id}
        data-state={isExpanded ? "open" : "closed"}
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <div
              className="flex items-center cursor-pointer select-none text-foreground hover:bg-muted/40 rounded group group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:p-1 group-data-[collapsible=icon]:overflow-hidden"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontFamily: 'Figtree, sans-serif' }}
            >
              <span className="flex items-center justify-center [&>svg]:size-4 [&>svg]:shrink-0">
                {grouping.icon || <ChevronRight className="h-4 w-4" />}
              </span>
              <span className="text-left truncate group-data-[collapsible=icon]:hidden" style={{ fontSize: '13px', fontWeight: 'normal', color: 'inherit' }}>
                {grouping.name}
              </span>
              <ChevronRight 
                className={cn(
                  "h-3 w-3 shrink-0 transition-transform duration-200 group-data-[collapsible=icon]:hidden ml-1",
                  isExpanded && "rotate-90"
                )} 
              />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="ml-4 pl-3 border-l border-muted-foreground/20 space-y-1 group-data-[collapsible=icon]:hidden">
              {regularItems.map(item => renderNavItem(item, channel, true))}
              
              {/* Team items section */}
              {hasTeamsAccess && teamItems.length > 0 && (
                <div className="mt-3 pt-2 border-t border-muted-foreground/20">
                  <div 
                    className="flex items-center select-none py-1 text-foreground mb-2"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontFamily: 'Figtree, sans-serif' }}
                  >
                    <span className="text-left" style={{ fontSize: '13px', fontWeight: 'normal' }}>Internal Only</span>
                  </div>
                  <div className="space-y-1">
                    {teamItems.map(item => renderNavItem(item, channel, true))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  const renderChannel = (channel: NavChannel) => {
    const isExpanded = expandedChannels[channel.id] ?? false
    const { regularItems, teamItems } = separateTeamItems(channel.items)
    const hasGroupings = channel.groupings && channel.groupings.length > 0
    const hasItems = channel.items && channel.items.length > 0

    // If channel name is "-", render items directly without channel header
    if (channel.name === "-") {
      return (
        <SidebarGroup key={channel.id} className="p-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {channel.items.map(item => renderNavItem(item, channel))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )
    }

    if (isCollapsed) {
      return (
        <SidebarGroup key={channel.id} className="p-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarMenuButton
                  onClick={() => {
                    onOptionChange(channel.name)
                    // Navigate to first item in channel
                    if (hasItems && regularItems.length > 0) {
                      onContainerChange(regularItems[0].clickEventName)
                    }
                  }}
                  isActive={false}
                  className={cn(
                    "h-10 w-10 p-0 justify-center mx-auto"
                  )}
                >
                  <span className="flex items-center justify-center text-xs font-medium">
                    {channel.icon != null ? channel.icon : (channel.name.charAt(0) ?? "")}
                  </span>
                </SidebarMenuButton>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                {channel.name}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </SidebarGroup>
      )
    }

    return (
      <Collapsible
        key={channel.id}
        open={isExpanded}
        onOpenChange={() => toggleChannel(channel.id)}
        className="group/collapsible"
        data-channel-id={channel.id}
        data-state={isExpanded ? "open" : "closed"}
      >
        <SidebarGroup className="p-1">
          <CollapsibleTrigger asChild>
            <div
              className="flex items-center gap-1 cursor-pointer select-none text-[#757271] hover:bg-muted/40 rounded group group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:overflow-hidden"
              style={{ display: 'flex', padding: '6px 8px', alignItems: 'center', gap: '4px', alignSelf: 'stretch', fontFamily: 'Figtree, sans-serif' }}
            >
              {channel.icon != null && (
                <span className="flex items-center justify-center [&>svg]:size-4 [&>svg]:shrink-0 group-data-[collapsible=icon]:hidden">
                  {channel.icon}
                </span>
              )}
              <span className="text-left font-medium uppercase tracking-wider group-data-[collapsible=icon]:hidden truncate" style={{ fontSize: '11px' }}>
                {channel.name}
              </span>
              <ChevronRight 
                className={cn(
                  "h-3 w-3 shrink-0 transition-transform duration-200 group-data-[collapsible=icon]:hidden ml-1",
                  isExpanded && "rotate-90"
                )}
              />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1 ml-3">
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {/* When channel has groupings: first item, then groupings, then remaining items (e.g. Overview then LIFT Analytics then rest) */}
                {hasGroupings && regularItems.length > 0 ? (
                  <>
                    {renderNavItem(regularItems[0], channel)}
                    {channel.groupings?.map(grouping => renderGrouping(grouping, channel))}
                    {regularItems.slice(1).map(item => renderNavItem(item, channel))}
                  </>
                ) : (
                  <>
                    {regularItems.map(item => renderNavItem(item, channel))}
                    {hasGroupings && channel.groupings?.map(grouping => renderGrouping(grouping, channel))}
                  </>
                )}
                
                {/* Team items section */}
                {hasTeamsAccess && teamItems.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-muted-foreground/20">
                    <div 
                      className="flex items-center select-none py-1 text-foreground mb-2"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontFamily: 'Figtree, sans-serif' }}
                    >
                      <span className="text-left" style={{ fontSize: '13px', fontWeight: 'normal' }}>Internal Only</span>
                    </div>
                    <div className="space-y-1">
                      {teamItems.map(item => renderNavItem(item, channel))}
                    </div>
                  </div>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>
    )
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {channels.map(channel => renderChannel(channel))}
    </div>
  )
}

export { iconMap }

